import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import userManagementApi from '../../services/userManagementApi';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';
import { ADMIN_PAGE_GROUPS, pageLabel } from '../../constants/adminPages';
import { EmptyState, LoadingState } from '../Common/PanelState';

const emptyForm = {
  name: '',
  email: '',
  password: '',
  role: 'staff',
  status: 'active',
  phone: '',
  allowed_pages: ['dashboard'],
};

function PageAccessPicker({ role, selected = [], onChange }) {
  if (role === 'admin') {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
        Admin ko saari pages ki access automatically milti hai.
      </div>
    );
  }

  const pages = Array.isArray(selected) ? selected : [];

  const toggle = (key) => {
    if (pages.includes(key)) {
      onChange(pages.filter((p) => p !== key));
    } else {
      onChange([...pages, key]);
    }
  };

  const toggleGroup = (group) => {
    const keys = group.pages.map((p) => p.key);
    const allOn = keys.every((k) => pages.includes(k));
    onChange(allOn ? pages.filter((k) => !keys.includes(k)) : [...new Set([...pages, ...keys])]);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3 max-h-72 overflow-y-auto">
      <p className="text-sm font-semibold text-gray-800">Page access</p>
      <p className="text-xs text-gray-500">Sirf selected pages sidebar mein dikhengi jab ye user login kare.</p>
      {ADMIN_PAGE_GROUPS.map((group) => {
        const keys = group.pages.map((p) => p.key);
        const allOn = keys.every((k) => pages.includes(k));
        return (
          <div key={group.id}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{group.label}</span>
              <button type="button" onClick={() => toggleGroup(group)} className="text-[11px] font-semibold text-red-600 hover:underline">
                {allOn ? 'Clear' : 'All'}
              </button>
            </div>
            <div className="space-y-1">
              {group.pages.map((page) => (
                <label key={page.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pages.includes(page.key)}
                    onChange={() => toggle(page.key)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span>{page.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function pagesSummary(user) {
  if (!user) return '—';
  if (String(user.role) === 'admin') return 'All pages';
  const pages = Array.isArray(user.allowed_pages) ? user.allowed_pages : [];
  if (pages.length === 0) return 'No pages';
  const labels = pages.map((key) => pageLabel(key));
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}

function Confirm({ isOpen, onClose, onConfirm, title, children }) {
  return (
    <ConfirmationModal isOpen={isOpen} onClose={onClose} onConfirm={onConfirm} title={title}>
      {children}
    </ConfirmationModal>
  );
}

function UserEditModal({ isOpen, onClose, userToEdit, onSave, onToggleStatus }) {
  const [form, setForm] = useState(emptyForm);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userToEdit) {
      setForm({
        name: userToEdit.name || '',
        email: userToEdit.email || '',
        password: '',
        role: userToEdit.role || 'staff',
        status: userToEdit.status || 'active',
        phone: userToEdit.phone || '',
        allowed_pages: Array.isArray(userToEdit.allowed_pages) && userToEdit.allowed_pages.length
          ? userToEdit.allowed_pages
          : ['dashboard'],
      });
      setNewPassword('');
    }
  }, [userToEdit]);

  if (!isOpen || !userToEdit) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name aur email required hain.');
      return;
    }
    if (form.role !== 'admin' && !(form.allowed_pages || []).length) {
      toast.error('Staff / editor ke liye kam az kam 1 page select karein.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        status: form.status,
        phone: form.phone || null,
        allowed_pages: form.role === 'admin' ? [] : (form.allowed_pages || []),
      };
      if (newPassword.trim()) {
        payload.password = newPassword.trim();
      }
      await onSave(userToEdit.id, payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-1">Edit User</h2>
        <p className="text-gray-500 mb-6 font-mono text-sm">{form.email}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input value={form.name} onChange={(e) => update('name', e.target.value)} className="w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={form.role} onChange={(e) => update('role', e.target.value)} className="w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500">
              <option value="editor">Editor</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <div className="flex gap-4 mt-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="u_status" checked={form.status === 'active'} onChange={() => update('status', 'active')} />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" name="u_status" checked={form.status === 'inactive'} onChange={() => update('status', 'inactive')} />
                <span className="text-sm text-gray-700">Inactive</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password (leave blank to keep old)</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="min 6 chars" className="w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" />
          </div>
        </div>

        <div className="mt-5">
          <PageAccessPicker
            role={form.role}
            selected={form.allowed_pages}
            onChange={(pages) => update('allowed_pages', pages)}
          />
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button onClick={onClose} className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300">Cancel</button>
          <button onClick={onToggleStatus} type="button" className="px-4 py-2 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600">
            {form.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-red-600 text-white rounded-lg font-semibold disabled:bg-gray-400" style={{ backgroundColor: saving ? '#a11217' : '#c4151c' }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserManagementSection() {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1, per_page: 15 });
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [deletingId, setDeletingId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  const [newForm, setNewForm] = useState({ ...emptyForm });
  const [submittingNew, setSubmittingNew] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page };
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await userManagementApi.list(params);
      setUsers(res.users);
      setPagination(res.pagination);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to load users.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, statusFilter, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const resetNew = () => setNewForm({ ...emptyForm });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim() || !newForm.email.trim() || !newForm.password?.trim()) {
      toast.error('Name, email, aur password required hain.');
      return;
    }
    if (newForm.role !== 'admin' && !(newForm.allowed_pages || []).length) {
      toast.error('Staff / editor ke liye kam az kam 1 page select karein.');
      return;
    }
    setSubmittingNew(true);
    try {
      await userManagementApi.create({
        name: newForm.name.trim(),
        email: newForm.email.trim(),
        password: newForm.password.trim(),
        role: newForm.role || 'staff',
        status: newForm.status || 'active',
        phone: newForm.phone?.trim() || null,
        allowed_pages: newForm.role === 'admin' ? [] : (newForm.allowed_pages || []),
      });
      toast.success(`User ${newForm.email} created.`);
      resetNew();
      setPage(1);
      loadUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors || err.message || 'User create nahi hua.';
      toast.error(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setSubmittingNew(false);
    }
  };

  const handleSaveEdit = async (id, payload) => {
    try {
      const res = await userManagementApi.update(id, payload);
      toast.success(res.message || 'User updated.');
      loadUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors || err.message || 'Update failed.';
      toast.error(typeof msg === 'object' ? JSON.stringify(msg) : msg);
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      const { message } = await userManagementApi.remove(deletingId);
      toast.success(message || 'User deleted.');
      setDeletingId(null);
      loadUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Delete failed.';
      toast.error(msg);
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async () => {
    if (!editingUser) return;
    const newStatus = editingUser.status === 'active' ? 'inactive' : 'active';
    try {
      await userManagementApi.update(editingUser.id, { status: newStatus });
      toast.success(`User marked ${newStatus}.`);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Status update failed.';
      toast.error(msg);
    }
  };

  const canMutate = (targetId) => {
    if (!user) return false;
    if (String(user.id) === String(targetId)) return false;
    return true;
  };

  const canDelete = (targetUser) => {
    if (!targetUser) return false;
    if (!canMutate(targetUser.id)) return false;
    if (String(targetUser.role) === 'admin') return false;
    return true;
  };

  const canEdit = (targetUser) => {
    if (!targetUser) return false;
    if (String(targetUser.id) === String(user?.id)) return true;
    return canMutate(targetUser.id);
  };

  return (
    <div className="container mx-auto">
      <Confirm
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        title="Delete User"
      >
        Are you sure you want to delete this user? Is action ko undo nahi kiya ja sakta.
      </Confirm>

      <UserEditModal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        userToEdit={editingUser}
        onSave={handleSaveEdit}
        onToggleStatus={handleToggleStatus}
      />

      <div className="mb-8">
        <h1 className="admin-page-title">User Management</h1>
        <p className="admin-page-sub">Staff ya editor ko specific pages assign karein — login ke baad unhe sirf wahi pages dikhengi.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-1 bg-white p-6 sm:p-8 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">Add New User</h2>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input value={newForm.name} onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={newForm.email} onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" value={newForm.password} onChange={(e) => setNewForm((f) => ({ ...f, password: e.target.value }))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" required minLength={6} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
              <input value={newForm.phone} onChange={(e) => setNewForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={newForm.role} onChange={(e) => setNewForm((f) => ({ ...f, role: e.target.value }))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500">
                  <option value="editor">Editor</option>
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={newForm.status} onChange={(e) => setNewForm((f) => ({ ...f, status: e.target.value }))} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <PageAccessPicker
              role={newForm.role}
              selected={newForm.allowed_pages}
              onChange={(pages) => setNewForm((f) => ({ ...f, allowed_pages: pages }))}
            />
            <button type="submit" disabled={submittingNew} className="w-full text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-transform hover:scale-[1.02]" style={{ backgroundColor: submittingNew ? '#a11217' : '#c4151c' }}>
              {submittingNew ? 'Adding User...' : 'Add User'}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-4 sm:p-6 border-b">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Existing Users</h2>
              <div className="text-sm text-gray-500">Total: {pagination.total}</div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Search (name/email/phone)</label>
                <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search..." className="w-full p-2 bg-gray-50 rounded-lg border border-gray-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
                <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }} className="w-full p-2 bg-gray-50 rounded-lg border border-gray-300">
                  <option value="">All</option>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="w-full p-2 bg-gray-50 rounded-lg border border-gray-300">
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={loadUsers} className="w-full px-4 py-2 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 text-gray-700">Refresh</button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <LoadingState label="Loading users" />
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pages</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length > 0 ? users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{u.name || '—'}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                        {u.phone && <div className="text-xs text-gray-400">{u.phone}</div>}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`capitalize px-3 py-1 text-xs font-semibold rounded-full ${u.role === 'admin' ? 'bg-red-100 text-red-800' : u.role === 'editor' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-800'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="text-xs text-gray-600 max-w-[180px]" title={pagesSummary(u)}>{pagesSummary(u)}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`capitalize px-3 py-1 text-xs font-semibold rounded-full ${u.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-center whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center justify-center gap-2">
                          {canEdit(u) ? (
                            <button
                              onClick={() => setEditingUser(u)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 text-xs font-semibold"
                              title="Edit user details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              Edit
                            </button>
                          ) : null}
                          {canDelete(u) ? (
                            <button
                              onClick={() => setDeletingId(u.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-xs font-semibold"
                              title="Delete user permanently"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              Delete
                            </button>
                          ) : (
                            !canEdit(u) && (
                              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gray-50 text-gray-400 border border-gray-200 text-xs font-medium" title={String(u.id) === String(user?.id) ? 'You cannot modify your own account' : 'Admin users cannot be deleted'}>
                                {String(u.id) === String(user?.id) ? '(current user)' : '🔒 Admin protected'}
                              </span>
                            )
                          )}
                          {canEdit(u) && !canDelete(u) && String(u.role) === 'admin' && String(u.id) !== String(user?.id) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-50 text-amber-600 border border-amber-200 text-[10px] font-semibold uppercase tracking-wide" title="Admin accounts cannot be deleted — deactivate or edit role instead">
                              Admin
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5"><EmptyState title="No users found" hint="Add a staff or editor account from the form." /></td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {pagination.last_page > 1 && (
            <div className="p-4 border-t flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-500">Page {pagination.current_page} / {pagination.last_page}</div>
              <div className="flex gap-2">
                <button disabled={pagination.current_page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1 bg-gray-100 rounded disabled:opacity-40 hover:bg-gray-200">Prev</button>
                <button disabled={pagination.current_page >= pagination.last_page} onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))} className="px-3 py-1 bg-gray-100 rounded disabled:opacity-40 hover:bg-gray-200">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserManagementSection;
